"use client";

import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PulsarService } from "@/services/pulsar.service";
import { getErrorMessage } from "@/helpers/getErrorMessage";
import { useAuthContext } from "@/contexts/auth-context";

// Constants for localStorage keys
const REFERRAL_STORAGE_KEY = 'pending_referral_code';

export const useReferral = (
  campaignId: string,
  groupId: string | undefined,
  address?: string,
  isAuthenticated: boolean = false
) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { accessToken } = useAuthContext();
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const isSessionReady = isAuthenticated && !!address && !!accessToken;

  // Get stored referral code
  const getStoredReferralCode = useCallback(() => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
        if (!stored) return null;
      const { code, campaign } = JSON.parse(stored);
      return campaign === campaignId ? code : null;
    } catch (error) {
      console.error('Error parsing localStorage:', error);
      return null;
    }
  }, [campaignId]);

  // Store referral code
  const storeReferralCode = useCallback(
    (code: string) => {
      localStorage.setItem(
        REFERRAL_STORAGE_KEY,
        JSON.stringify({ code, campaign: campaignId })
      );
      setReferralCode(code);
    },
    [campaignId]
  );

  // Clear stored referral code
  const clearStoredReferralCode = useCallback(() => {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
    setReferralCode(null);
  }, []);

  // Remove referral code from URL
  const removeReferralFromUrl = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.delete('referralCode');
    const query = newSearchParams.toString();
    const newUrl = query ? `${pathname}?${query}` : pathname;
    router.replace(newUrl);
  }, [pathname, router, searchParams]);

  // Initialize referral code from URL or localStorage
  useEffect(() => {
    const urlReferralCode = searchParams.get('referralCode');

    if (urlReferralCode) {
      storeReferralCode(urlReferralCode);
      // TODO: Add Plausible analytics
      // plausible('Referral: Open', {
      //   props: { referralCode: urlReferralCode },
      // });
    } else {
      const storedCode = getStoredReferralCode();
      if (storedCode) {
        setReferralCode(storedCode);
      }
    }
  }, [searchParams, getStoredReferralCode, storeReferralCode, pathname]);

  // Show modal when authenticated and have referral code
  useEffect(() => {
    
    if (isSessionReady) {
      // If we don't have a referral code in state, try to get it from localStorage
      if (!referralCode) {
        const storedCode = getStoredReferralCode();
        if (storedCode) {
          setReferralCode(storedCode);
          return; // Let the next effect handle the modal
        }
      }
      
      if (referralCode) {
        // Add a small delay to ensure authentication is fully complete
        const timer = setTimeout(() => {
          setIsOpen(true);
        }, 1000); // 1 second delay
        
        return () => clearTimeout(timer);
      }
    }
  }, [isSessionReady, referralCode, getStoredReferralCode]);

  const referralMutation = useMutation({
    mutationFn: async (code: string) => {
      if (!isSessionReady) {
        throw new Error("Please connect your wallet before applying a referral code.");
      }

      await PulsarService.createReferral(campaignId, groupId, code);

      toast.success('Referral code applied successfully!');
      clearStoredReferralCode();
      removeReferralFromUrl();
      
      // Invalidate user data to reflect new referral status
      queryClient.invalidateQueries({
        queryKey: ["challenges", "user", campaignId, groupId || ""],
      });
      
      // TODO: Add Plausible analytics
      // plausible('Referral: Apply', {
      //   props: { referralCode: referralCode },
      // });
      
      setIsOpen(false);
    },
    onError: (error: unknown) => {
      console.error('Referral application error:', error);
      toast.error(getErrorMessage(error, 'Failed to apply referral code. Please try again.'));
    },
  });

  const handleClose = useCallback(() => {
    removeReferralFromUrl();
    setReferralCode(null);
    clearStoredReferralCode();
    setIsOpen(false);
    
    // TODO: Add Plausible analytics
    // plausible('Referral: Close', {
    //   props: { referralCode: referralCode },
    // });
  }, [removeReferralFromUrl, clearStoredReferralCode]);

  const handleReject = useCallback(() => {
    clearStoredReferralCode();
    removeReferralFromUrl();
    setIsOpen(false);
  }, [clearStoredReferralCode, removeReferralFromUrl]);

  return {
    isOpen,
    onClose: handleClose,
    onReject: handleReject,
    referralCode,
    applyReferral: referralMutation.mutateAsync,
    isLoading: referralMutation.isPending,
    setIsOpen, // Expose for debugging
  };
};
